import React from "react";
import { View, StyleSheet } from "react-native";
import NtRootPageContainer from "../../root/components/NtRootPageContainer";
import BaseController from "../../common/base/BaseController";
import { fetchRegistrations } from "../../api/extension";
import Logger from "../../common/utils/Logger";
import settings from "../../config/settings";
import { asyncStorageSetData } from "../../common/utils/ControllerUtils";
import NtListHeader from "../../components/NtListHeader";
import NtListEmpty from "../../components/NtListEmpty";
import NtListRow from "../../components/NtListRow";
import NtExtensionTag from "../../components/NtExtensionTag";
import NtPageActionButton from "../../components/NtPageActionButton";
import NtPageSection from "../../components/NtPageSection";
import NtSearchBar from "../../components/NtSearchBar";
import NtText from "../../components/NtText";
import { useTheme } from "../../context/ThemeContext";
import registrationcsvcolumndata from "../data/registrationcsvcolumndata";

function StatusStat({ count, label, color }) {
  const { theme } = useTheme();
  return (
    <View style={{ flexDirection: "row", alignItems: "baseline", marginRight: 18 }}>
      <NtText style={{ color, fontWeight: "700", fontSize: 16, fontVariant: ["tabular-nums"] }}>
        {count}
      </NtText>
      <NtText style={{ fontSize: 11, color: theme.textDim, marginLeft: 4 }}>
        {label}
      </NtText>
    </View>
  );
}

function StatusStatsConsumer({ data }) {
  const { theme } = useTheme();
  const isUnstable = (item) => {
    if (item?.pingstatus && /slow|high|unreach/i.test(item.pingstatus)) return true;
    const pt = parseFloat(item?.pingtime);
    return Number.isFinite(pt) && pt >= 100;
  };

  const unstable = data.filter(isUnstable).length;
  const online = data.length - unstable;

  return (
    <View style={{ flexDirection: "row", alignItems: "center" }}>
      <StatusStat count={online} label="online" color={theme.green} />
      <StatusStat count={unstable} label="unstable" color={theme.yellow} />
    </View>
  );
}

/**
 * Registrations aren't server-paginated — we fetch once and filter client-side.
 * NtPagination is therefore omitted in favor of a live "Refresh" action.
 */
class RegistrationController extends BaseController {
  state = {
    ...super.state,
    loading: false,
    data: [],
    master: [],
    searchText: "",
  };

  column_location = "@registration_column_storage";

  componentDidMount() {
    this.handleDefaultColumnsFetch(
      this.column_location,
      registrationcsvcolumndata.filter((each) => each.default === true),
    );
    this.handleDataFetch();
  }


  handleDataFetch = async () => {
    this.setState({ loading: true });
    const response = await fetchRegistrations(this.context.user?.domain);
    Logger("Registrations fetch response", response);
    if (response.ok) {
      this.setState({ data: response.data || [], master: response.data || [] });
    }
    this.setState({ loading: false });
  };

  handleExportFetch = async () => {
    const response = await fetchRegistrations(this.context.user?.domain);
    return response.ok ? response.data : [];
  };

  handleExportDataParce = (item, column) => item[column];

  handleSearch = (text) => {
    this.setState({ searchText: text });
    if (text?.length > 2) {
      const lower = text.toLowerCase();
      this.setState({
        data: this.state.master?.filter(
          (each) =>
            each.user?.toLowerCase()?.includes(lower) ||
            each.agent?.toLowerCase()?.includes(lower),
        ),
      });
    } else {
      this.setState({ data: this.state.master });
    }
  };

  renderRow = (item, index) => (
    <NtListRow
      key={(item.user || "") + index}
      item={item}
      columns={this.state.columns}
      renderCustom={(row, column) => {
        if (column === "username") {
          // username on registrations comes through as `1234@domain.com`;
          // strip the domain so the tag stays compact.
          const num = (row[column] || "").split("@")[0];
          return <NtExtensionTag extension={num} />;
        }
      }}
    />
  );

  render() {
    const count = this.state.master?.length || 0;
    const subtitle = `Currently registered SIP devices and their status · ${count} registration${count === 1 ? "" : "s"}`;
    const actions = (
      <NtPageActionButton
        icon="refresh"
        title="Refresh"
        onPress={() => this.handleDataFetch()}
      />
    );
    const searchBar = (
      <NtSearchBar value={this.state.searchText} onChangeText={this.handleSearch} />
    );
    const stats = <StatusStatsConsumer data={this.state.master || []} />;

    return (
      <NtRootPageContainer
        pageTitle="Registrations"
        pageSubtitle={subtitle}
        permission={this.handlePermissionCheck()}
        actions={actions}
      >
        <NtPageSection filters={stats} search={searchBar}>
          <View style={styles.tableWrap}>
            <NtListHeader
              columns={registrationcsvcolumndata}
              selectedColumns={this.state.columns}
              onColumnUpdate={(updated) => {
                asyncStorageSetData(this.column_location, updated);
                this.setState({ columns: updated });
              }}
              exportFileName="registration"
              exportDataParser={this.handleExportDataParce}
              exportFetchDataAsync={this.handleExportFetch}
            />
            <NtListEmpty visible={this.state.data?.length === 0} />
            {this.state.data?.map((each, index) => this.renderRow(each, index))}
          </View>
        </NtPageSection>
      </NtRootPageContainer>
    );
  }
}

const styles = StyleSheet.create({
  tableWrap: { paddingHorizontal: 14, paddingVertical: 4 },
});

export default RegistrationController;
